import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  approvePurchaseOrder,
  cancelPurchaseOrder,
  getPurchaseOrder,
  getTenantFeatures,
  listBranches,
  refillPurchaseOrder,
  type PurchaseOrderDetail,
  type PurchaseOrderLine,
} from '../lib/api';
import { useHasPerm } from '../lib/auth-store';
import { StatusBadge } from './purchase-orders';

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => getPurchaseOrder(id!),
    enabled: !!id,
  });
  const [refilling, setRefilling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  // "Return to supplier" lives on the accounting side: the RTS page needs
  // `view.accounting` for every read, so both perms gate the button.
  const canWriteReturn = useHasPerm('supplier_return.write');
  // Approve / cancel / receive are `purchase_order.write` server-side.
  const canWritePo = useHasPerm('purchase_order.write');
  const canViewAccounting = useHasPerm('view.accounting');
  const featuresQ = useQuery({
    queryKey: ['tenant-features'],
    queryFn: getTenantFeatures,
    staleTime: 60_000,
    retry: false,
  });
  const accountingEnabled = featuresQ.data?.accounting_enabled === true;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['purchase-order', id] });
    qc.invalidateQueries({ queryKey: ['purchase-orders'] });
  };
  const approve = useMutation({
    mutationFn: () => approvePurchaseOrder(id!),
    meta: { successMessage: 'Purchase order approved.' },
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: () => cancelPurchaseOrder(id!, cancelReason.trim()),
    meta: { successMessage: 'Purchase order cancelled.' },
    onSuccess: () => {
      invalidate();
      setShowCancel(false);
    },
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const po = query.data;
  if (!po) return <p className="text-sm text-muted-foreground">Purchase order not found.</p>;

  const hasFulfillments = po.fulfillments.length > 0;
  const canApprove = canWritePo && po.status === 'pending';
  const canRefill = canWritePo && (po.status === 'approved' || po.status === 'incomplete');
  const canCancel =
    canWritePo &&
    (po.status === 'pending' || po.status === 'approved' || po.status === 'incomplete') &&
    !hasFulfillments;
  const canReturn =
    accountingEnabled &&
    canWriteReturn &&
    canViewAccounting &&
    (po.status === 'incomplete' || po.status === 'completed') &&
    po.lines.some((l) => l.qtyFulfilled - l.qtyReturned > 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Purchase Order Detail</h1>
        <Link to="/purchase-orders" className="text-sm text-primary">
          ← Back to list
        </Link>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xl">{po.poNumber}</CardTitle>
          <StatusBadge status={po.status} />
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          <div>
            <span className="text-muted-foreground">Supplier:</span> {po.supplierName}
          </div>
          <div>
            <span className="text-muted-foreground">Created by:</span> {po.createdByName ?? '—'}
          </div>
          <div>
            <span className="text-muted-foreground">Created:</span>{' '}
            {new Date(po.createdAt).toLocaleString()}
          </div>
          {po.memo && (
            <div className="md:col-span-2">
              <span className="text-muted-foreground">Memo:</span> {po.memo}
            </div>
          )}
          {po.cancelledReason && (
            <div className="md:col-span-2 text-destructive">Cancelled: {po.cancelledReason}</div>
          )}
        </CardContent>
      </Card>

      <Timeline po={po} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Products</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Product</th>
                <th className="px-3 py-2 font-medium">SKU</th>
                <th className="px-3 py-2 text-right font-medium">Ordered</th>
                <th className="px-3 py-2 text-right font-medium">Fulfilled</th>
                <th className="px-3 py-2 text-right font-medium">Returned</th>
                <th className="px-3 py-2 text-right font-medium">Remaining</th>
              </tr>
            </thead>
            <tbody>
              {po.lines.map((l, i) => {
                const remaining = l.qtyOrdered - l.qtyFulfilled;
                return (
                  <tr key={l.id} className="border-t">
                    <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-2">
                      {l.productName}
                      {l.variantName && (
                        <span className="text-muted-foreground"> — {l.variantName}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{l.sku}</td>
                    <td className="px-3 py-2 text-right">{l.qtyOrdered}</td>
                    <td className="px-3 py-2 text-right">{l.qtyFulfilled}</td>
                    <td className="px-3 py-2 text-right">
                      {l.qtyReturned > 0 ? (
                        l.qtyReturned
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td
                      className={`px-3 py-2 text-right ${remaining > 0 ? 'font-medium text-amber-600' : 'text-muted-foreground'}`}
                    >
                      {remaining}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {hasFulfillments && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fulfillment History</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {po.fulfillments.map((f, i) => (
              <div key={f.id} className="rounded-md border p-3 text-sm">
                <div className="mb-1 font-medium">
                  Fulfillment #{i + 1} → {f.branchName}{' '}
                  <span className="font-normal text-muted-foreground">
                    {new Date(f.createdAt).toLocaleString()} · {f.fulfilledByName ?? '—'}
                  </span>
                </div>
                <table className="w-full">
                  <tbody>
                    {f.lines.map((fl) => {
                      const line = po.lines.find((l) => l.id === fl.poLineId);
                      return (
                        <tr key={fl.poLineId}>
                          <td className="py-0.5 text-muted-foreground">
                            {line
                              ? line.variantName
                                ? `${line.productName} — ${line.variantName}`
                                : line.productName
                              : fl.poLineId}
                          </td>
                          <td className="py-0.5 text-right">{fl.qty}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Action bar */}
      <Card>
        <CardContent className="flex flex-wrap justify-end gap-2 pt-6">
          {canReturn && (
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/accounting/supplier-returns?po=${po.id}&supplier=${po.supplierId}`)
              }
            >
              Return to supplier
            </Button>
          )}
          {canCancel && (
            <Button variant="outline" onClick={() => setShowCancel((v) => !v)}>
              Cancel PO
            </Button>
          )}
          {canApprove && (
            <Button onClick={() => approve.mutate()} disabled={approve.isPending}>
              {approve.isPending ? 'Approving…' : 'Approve'}
            </Button>
          )}
          {canRefill && (
            <Button onClick={() => setRefilling(true)}>Complete &amp; Refill Inventory</Button>
          )}
          {errText(approve.error) && (
            <p className="w-full text-right text-sm text-destructive">{errText(approve.error)}</p>
          )}
        </CardContent>
      </Card>

      {showCancel && (
        <Card>
          <CardContent className="space-y-2 pt-6">
            <label className="text-xs text-muted-foreground">Cancellation reason</label>
            <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
            {errText(cancel.error) && (
              <p className="text-sm text-destructive">{errText(cancel.error)}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCancel(false)}>
                Keep PO
              </Button>
              <Button
                variant="destructive"
                disabled={!cancelReason.trim() || cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                {cancel.isPending ? 'Cancelling…' : 'Confirm cancel'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {refilling && (
        <RefillDialog
          po={po}
          onClose={() => setRefilling(false)}
          onDone={() => {
            setRefilling(false);
            invalidate();
          }}
        />
      )}

      <button hidden onClick={() => navigate('/purchase-orders')} />
    </div>
  );
}

function Timeline({ po }: { po: PurchaseOrderDetail }) {
  const approved = po.status !== 'pending' && po.status !== 'cancelled';
  const done = po.status === 'completed';
  const partial = po.status === 'incomplete';
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Status Timeline</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <Step done label="Created" sub={`By: ${po.createdByName ?? '—'}`} />
        <Step
          done={approved}
          label="Approved"
          sub={
            po.status === 'cancelled'
              ? 'Cancelled before approval'
              : approved
                ? po.approvedAt
                  ? new Date(po.approvedAt).toLocaleString()
                  : 'Approved'
                : 'Waiting for approval'
          }
        />
        <Step
          done={done}
          partial={partial}
          label="Completed (Inventory Refilled)"
          sub={
            done
              ? 'All items received'
              : partial
                ? 'Some items received. Awaiting remaining quantities.'
                : 'Waiting for completion'
          }
        />
      </CardContent>
    </Card>
  );
}

function Step({
  done,
  partial,
  label,
  sub,
}: {
  done?: boolean;
  partial?: boolean;
  label: string;
  sub: string;
}) {
  const color = done ? 'bg-green-500' : partial ? 'bg-teal-500' : 'bg-gray-300';
  return (
    <div className="flex gap-3">
      <div className={`mt-0.5 h-5 w-5 shrink-0 rounded-full ${color}`} />
      <div>
        <div className="font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </div>
    </div>
  );
}

function RefillDialog({
  po,
  onClose,
  onDone,
}: {
  po: PurchaseOrderDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const branchesQ = useQuery({ queryKey: ['branches'], queryFn: listBranches });
  const branches = branchesQ.data ?? [];
  const outstanding = po.lines.filter((l) => l.qtyOrdered - l.qtyFulfilled > 0);
  const [branchId, setBranchId] = useState('');
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(outstanding.map((l) => [l.id, String(l.qtyOrdered - l.qtyFulfilled)])),
  );
  // Stock lots: each received line becomes a lot at the PO's own unit cost, so
  // all that is left to say is when it expires and what the lot is called.
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const lotsEnabled = featuresQ.data?.inventory_lots_enabled === true;
  const [expiresOn, setExpiresOn] = useState<Record<string, string>>({});
  const [lotNo, setLotNo] = useState<Record<string, string>>({});

  const refill = useMutation({
    mutationFn: () =>
      refillPurchaseOrder(po.id, {
        branchId,
        lines: outstanding
          .map((l) => ({
            poLineId: l.id,
            qty: Number(qty[l.id] ?? '0'),
            ...(lotsEnabled && expiresOn[l.id] ? { expiresOn: expiresOn[l.id] } : {}),
            ...(lotsEnabled && lotNo[l.id]?.trim() ? { lotNo: lotNo[l.id]!.trim() } : {}),
          }))
          .filter((l) => l.qty > 0),
      }),
    meta: { successMessage: 'Inventory received.' },
    onSuccess: onDone,
  });

  function remaining(l: PurchaseOrderLine) {
    return l.qtyOrdered - l.qtyFulfilled;
  }
  const overSome = outstanding.some((l) => Number(qty[l.id] ?? '0') > remaining(l));
  const anyQty = outstanding.some((l) => Number(qty[l.id] ?? '0') > 0);
  const canSubmit = !!branchId && anyQty && !overSome && !refill.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Complete &amp; Refill Inventory</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Receiving branch / stockist</label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="h-9 w-full max-w-md rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">— Select branch —</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1 pr-2">Item</th>
              <th className="py-1 pr-2 text-right">Remaining</th>
              <th className="py-1 pr-2 text-right">Receive now</th>
              {lotsEnabled && <th className="py-1 pr-2">Expires on</th>}
              {lotsEnabled && <th className="py-1 pr-2">Lot number</th>}
            </tr>
          </thead>
          <tbody>
            {outstanding.map((l) => (
              <tr key={l.id} className="border-t">
                <td className="py-1 pr-2">
                  {l.productName}
                  {l.variantName && (
                    <span className="text-muted-foreground"> — {l.variantName}</span>
                  )}
                </td>
                <td className="py-1 pr-2 text-right text-muted-foreground">{remaining(l)}</td>
                <td className="py-1 pr-2 text-right">
                  <Input
                    className="ml-auto h-8 w-20"
                    value={qty[l.id] ?? ''}
                    inputMode="numeric"
                    onChange={(e) => setQty((p) => ({ ...p, [l.id]: e.target.value }))}
                  />
                </td>
                {lotsEnabled && (
                  <td className="py-1 pr-2">
                    <Input
                      type="date"
                      className="h-8 w-40"
                      value={expiresOn[l.id] ?? ''}
                      onChange={(e) => setExpiresOn((p) => ({ ...p, [l.id]: e.target.value }))}
                      aria-label={`Expiry date for ${l.productName}`}
                    />
                  </td>
                )}
                {lotsEnabled && (
                  <td className="py-1 pr-2">
                    <Input
                      className="h-8 w-40"
                      value={lotNo[l.id] ?? ''}
                      maxLength={60}
                      placeholder="receipt number"
                      onChange={(e) => setLotNo((p) => ({ ...p, [l.id]: e.target.value }))}
                      aria-label={`Lot number for ${l.productName}`}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {lotsEnabled && (
          <p className="text-xs text-muted-foreground">
            Each line becomes a stock lot at this order's unit cost. Leave the expiry blank to use
            the product's shelf life, and the lot number blank to use the receipt number.
          </p>
        )}
        {overSome && (
          <p className="text-sm text-destructive">A quantity exceeds what's remaining.</p>
        )}
        {errText(refill.error) && (
          <p className="text-sm text-destructive">{errText(refill.error)}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => refill.mutate()}>
            {refill.isPending ? 'Receiving…' : 'Receive into branch'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
